/**
 * TagField — one of the four study tags (sponsor, CRO, therapeutic area,
 * indication; plan §5.3, §7.5). A free-text box that resolves server-side
 * through WP-33a's matching: submitting the form sends this field's text
 * (or, once a candidate is picked, that candidate's entry id) and the
 * backend either resolves it or answers 409 with ranked candidates (plan
 * §13.4 WP-34: "{ error, field, candidates }"). No score is ever auto-
 * matched here (G11) — a person always picks, or keeps typing.
 */
import { fieldCls } from '../../../ui';
import type { TagMatchCandidate } from '../../api/client';

export interface TagFieldState {
  /** What the person typed, or a picked candidate's official name (for display). */
  text: string;
  /** Set once a candidate is picked — this is what gets sent, verbatim, as the entry id. */
  resolvedId: string | null;
  /** Present after a 409 named this field; null before any submit attempt. */
  candidates: TagMatchCandidate[] | null;
}

export const EMPTY_TAG_FIELD: TagFieldState = { text: '', resolvedId: null, candidates: null };

interface Props {
  id: string;
  label: string;
  state: TagFieldState;
  onTextChange: (text: string) => void;
  onPick: (candidate: TagMatchCandidate) => void;
}

export function TagField({ id, label, state, onTextChange, onPick }: Props) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={state.text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder={`Type a ${label.toLowerCase()}, or paste an entry id`}
        className={`${fieldCls} w-full`}
      />
      {state.resolvedId && (
        <p className="mt-1 text-xs text-primary-700 dark:text-primary-400">Matched to an existing master-list entry.</p>
      )}
      {state.candidates && state.candidates.length > 0 && (
        <div className="mt-2 space-y-1 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
          <p className="px-1 text-xs text-gray-500 dark:text-gray-400">
            Did you mean one of these? Pick one, or keep typing to try a different match.
          </p>
          {state.candidates.map((c) => (
            <button
              key={c.entry.id}
              type="button"
              onClick={() => onPick(c)}
              className="block w-full rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-primary-900/20"
            >
              {c.entry.official_name}
            </button>
          ))}
        </div>
      )}
      {state.candidates && state.candidates.length === 0 && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          No close matches in the master list. Try different wording, or add it from the Admin tab first.
        </p>
      )}
    </div>
  );
}

export default TagField;
